import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import HistoryIcon from "@mui/icons-material/History";
import MoveDownIcon from "@mui/icons-material/MoveDown";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SupervisedUserCircleIcon from "@mui/icons-material/SupervisedUserCircle";

import type { AssignedLimitationInput, LimitationDefinition } from "src/shared/api-types/limitations";

import { StyledSwitch } from "../../../../components/Base";
import type { LinkedOperation } from "../types";
import OperationLimitationsCell from "./OperationLimitationsCell";
import OperationLimitationsDialog from "./OperationLimitationsDialog";
import HistoryDialog from "./HistoryDialog";
import CopyConfirmDialog from "./CopyConfirmDialog";

const cellStyle: React.CSSProperties = { border: 0 };
const switchCellStyle: React.CSSProperties = { border: 0, textAlign: "center" };

interface OperationRowProps {
  operation: LinkedOperation;
  userId: number;
  crewId: number;
  aircraftTypeName: string;
  canEdit: boolean;
  limitationDefinitions: LimitationDefinition[];
  onToggle: (
    typeId: number,
    operationId: number,
    enabled: boolean,
    underSupervision: boolean,
    limitations?: AssignedLimitationInput[],
  ) => Promise<unknown>;
  onCopy: (operation: LinkedOperation) => void;
  showCopy: boolean;
  otherOperationNames: string[];
  isFiltered: boolean;
}

const OperationRow: React.FC<OperationRowProps> = ({
  operation,
  userId,
  crewId,
  aircraftTypeName,
  canEdit,
  limitationDefinitions,
  onToggle,
  onCopy,
  showCopy,
  otherOperationNames,
  isFiltered,
}) => {
  const { t } = useTranslation();
  const [showHistory, setShowHistory] = useState(false);
  const [showCopyConfirm, setShowCopyConfirm] = useState(false);
  const [showLimitationsDialog, setShowLimitationsDialog] = useState(false);

  const handleEnabledChange = () => {
    const newEnabled = !operation.enabled;
    const newSupervision = newEnabled ? operation.underSupervision : false;
    onToggle(operation.typeId, operation.operationId, newEnabled, newSupervision);
  };

  const handleSupervisionChange = () => {
    onToggle(operation.typeId, operation.operationId, operation.enabled, !operation.underSupervision);
  };

  const handleCopyConfirm = () => {
    setShowCopyConfirm(false);
    onCopy(operation);
  };

  const handleSaveLimitations = (limitations: AssignedLimitationInput[]) => {
    setShowLimitationsDialog(false);
    onToggle(operation.typeId, operation.operationId, operation.enabled, operation.underSupervision, limitations);
  };

  return (
    <>
      <TableRow>
        <TableCell style={cellStyle}>{operation.operationName}</TableCell>
        <TableCell style={switchCellStyle}>
          {canEdit ? (
            <StyledSwitch
              checked={operation.enabled}
              disabled={operation.saving}
              onChange={handleEnabledChange}
              title={t("aircraft_management.enabled")}
            />
          ) : operation.enabled ? (
            <CheckCircleIcon fontSize="small" color="success" titleAccess={t("aircraft_management.enabled")} />
          ) : (
            <CancelIcon fontSize="small" color="disabled" titleAccess={t("aircraft_management.enabled")} />
          )}
        </TableCell>
        <TableCell style={switchCellStyle}>
          {canEdit ? (
            <StyledSwitch
              checked={operation.underSupervision}
              disabled={!operation.enabled || operation.saving}
              onChange={handleSupervisionChange}
              title={t("aircraft_management.under_supervision")}
            />
          ) : operation.underSupervision ? (
            <SupervisedUserCircleIcon
              fontSize="small"
              color="info"
              titleAccess={t("aircraft_management.under_supervision")}
            />
          ) : (
            <CancelIcon fontSize="small" color="disabled" titleAccess={t("aircraft_management.under_supervision")} />
          )}
        </TableCell>
        <TableCell style={{ ...cellStyle, textAlign: "center" }}>
          <OperationLimitationsCell
            enabled={operation.enabled}
            limitations={operation.limitations}
            canEdit={canEdit}
            onClick={() => setShowLimitationsDialog(true)}
          />
        </TableCell>
        <TableCell style={{ ...cellStyle, verticalAlign: "middle", textAlign: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
            <IconButton size="small" title={t("aircraft_management.history")} onClick={() => setShowHistory(true)}>
              <HistoryIcon fontSize="small" />
            </IconButton>
            {showCopy && (
              <IconButton
                size="small"
                title={
                  isFiltered
                    ? t("aircraft_management.copy_tooltip_filtered", { aircraft_type: aircraftTypeName })
                    : t("aircraft_management.copy_tooltip", { aircraft_type: aircraftTypeName })
                }
                onClick={() => setShowCopyConfirm(true)}
              >
                <MoveDownIcon fontSize="small" sx={{ transform: "scaleX(-1)" }} />
              </IconButton>
            )}
          </Box>
        </TableCell>
      </TableRow>

      <HistoryDialog
        open={showHistory}
        onClose={() => setShowHistory(false)}
        userId={userId}
        crewId={crewId}
        typeId={operation.typeId}
        operationId={operation.operationId}
        operationName={operation.operationName}
        canEdit={canEdit}
      />

      <CopyConfirmDialog
        open={showCopyConfirm}
        operationName={operation.operationName}
        enabled={operation.enabled}
        underSupervision={operation.underSupervision}
        limitations={operation.limitations}
        otherOperationNames={otherOperationNames}
        isFiltered={isFiltered}
        onConfirm={handleCopyConfirm}
        onClose={() => setShowCopyConfirm(false)}
      />

      <OperationLimitationsDialog
        open={showLimitationsDialog}
        definitions={limitationDefinitions}
        assignedLimitations={operation.limitations}
        onClose={() => setShowLimitationsDialog(false)}
        onSave={handleSaveLimitations}
      />
    </>
  );
};

export default OperationRow;
